import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import WarningBanner from '@/components/WarningBanner'
import {
  JournalEntryCard,
  JournalEntryDocChip,
  JournalEntryExternalLink,
  JournalEntryPostedBadge,
} from '@cottage/design-system/internal-tooling/ui'
import { useDefaultIncompleteReportMonth } from '@/hooks/useDefaultIncompleteReportMonth'
import { formatJePostedDate, reportMonthLabel } from '@/pages/distribution-waterfall/waterfallJeStatus'
import {
  AllocationPreviewCards,
  UhcAuditEmployeeIssues,
  UhcAuditNotesList,
} from '@/pages/uhcAuditDisplay'
import { postJournalEntry } from '@/services/accounting'
import {
  auditUhcMonth,
  createUhcMonthDraft,
  deleteUhcJournalEntryFromQbo,
  deleteUhcMonthFile,
  downloadUhcMonthFile,
  getUhcMonth,
  getUhcMonthsSummary,
  uploadUhcMonthFile,
  validateUhcBankTransaction,
  type UhcAuditResponse,
  type UhcDraftResponse,
  type UhcMonthDetailResponse,
} from '@/services/uhcAllocations'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { countNonCompleteMonthOptions } from '@/utils/allocationFormat'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import { mergeUhcMonthSelectOptions } from './uhcMonthSelect'

type Props = {
  canEdit: boolean
  onAddMissing?: (names: string[]) => void
  addingMissing?: boolean
}

function formatUsd(value: number | string | undefined | null): string {
  if (value == null) return '—'
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  })
}

function monthEndIso(reportMonth: string): string {
  const [yearStr, monthStr] = reportMonth.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const last = new Date(year, month, 0)
  return last.toISOString().slice(0, 10)
}

export default function UhcAllocate({ canEdit, onAddMissing, addingMissing }: Props) {
  const setBenefitsCount = useMonthCloseNavStore((s) => s.setBenefitsCount)
  const selectedMonthRef = useRef<string | null>(null)
  const [monthsSummary, setMonthsSummary] = useState<
    Awaited<ReturnType<typeof getUhcMonthsSummary>>['months']
  >([])
  const [monthsSummaryReady, setMonthsSummaryReady] = useState(false)
  const [monthDetail, setMonthDetail] = useState<UhcMonthDetailResponse | null>(null)
  const [monthLoading, setMonthLoading] = useState(false)
  const [audit, setAudit] = useState<UhcAuditResponse | null>(null)
  const [draft, setDraft] = useState<UhcDraftResponse | null>(null)
  const [jeDate, setJeDate] = useState('')
  const [bankStatus, setBankStatus] = useState<string | null>(null)
  const [auditing, setAuditing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [creatingDraft, setCreatingDraft] = useState(false)
  const [posting, setPosting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [postedMenuOpen, setPostedMenuOpen] = useState(false)
  const [validatingBank, setValidatingBank] = useState(false)

  const monthSelectOptions = useMemo(
    () => mergeUhcMonthSelectOptions(monthsSummary),
    [monthsSummary],
  )

  const [selectedMonth, setSelectedMonth] = useDefaultIncompleteReportMonth(
    monthSelectOptions,
    monthsSummaryReady,
  )
  selectedMonthRef.current = selectedMonth

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await getUhcMonthsSummary()
      setMonthsSummary(data.months)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load months'))
      setMonthsSummary([])
    } finally {
      setMonthsSummaryReady(true)
    }
  }, [])

  useEffect(() => {
    if (!monthsSummaryReady) return
    setBenefitsCount(countNonCompleteMonthOptions(monthSelectOptions))
  }, [monthsSummaryReady, monthSelectOptions, setBenefitsCount])

  const loadMonthDetail = useCallback(async (reportMonth: string) => {
    setMonthLoading(true)
    try {
      const detail = await getUhcMonth(reportMonth)
      if (selectedMonthRef.current !== reportMonth) return
      setMonthDetail(detail)
      setAudit(detail.audit)
      setDraft(detail.draft)
      setJeDate(detail.je_date ?? monthEndIso(reportMonth))
      setBankStatus(null)
    } catch (err) {
      if (selectedMonthRef.current === reportMonth) {
        toast.error(formatApiError(err, 'Failed to load month'))
        setMonthDetail(null)
      }
    } finally {
      if (selectedMonthRef.current === reportMonth) setMonthLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadMonthsSummary()
  }, [loadMonthsSummary])

  useEffect(() => {
    if (!selectedMonth) return
    void loadMonthDetail(selectedMonth)
  }, [selectedMonth, loadMonthDetail])

  async function handleUpload(kind: 'invoice' | 'adjustment', file: File) {
    if (!canEdit || !selectedMonth) return
    try {
      setUploading(true)
      await uploadUhcMonthFile(selectedMonth, file, kind)
      await loadMonthDetail(selectedMonth)
      await loadMonthsSummary()
      toast.success('File uploaded')
    } catch (err) {
      toast.error(formatApiError(err, 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  async function handleDeleteFile(fileId: number) {
    if (!canEdit || !selectedMonth) return
    try {
      await deleteUhcMonthFile(selectedMonth, fileId)
      await loadMonthDetail(selectedMonth)
      toast.success('File removed')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete file'))
    }
  }

  async function handleDownload(fileId: number, filename: string) {
    if (!selectedMonth) return
    try {
      const blob = await downloadUhcMonthFile(selectedMonth, fileId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(formatApiError(err, 'Download failed'))
    }
  }

  async function handleAudit() {
    if (!canEdit || !selectedMonth) return
    try {
      setAuditing(true)
      const result = await auditUhcMonth(selectedMonth)
      setAudit(result)
      await loadMonthDetail(selectedMonth)
      toast.success('Audit complete')
    } catch (err) {
      toast.error(formatApiError(err, 'Audit failed'))
    } finally {
      setAuditing(false)
    }
  }

  async function handleCreateDraft() {
    if (!canEdit || !selectedMonth || !jeDate) return
    try {
      setCreatingDraft(true)
      const result = await createUhcMonthDraft(selectedMonth, jeDate)
      setDraft(result)
      await loadMonthDetail(selectedMonth)
      await loadMonthsSummary()
      toast.success('Journal entry draft saved')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to generate draft'))
    } finally {
      setCreatingDraft(false)
    }
  }

  async function handlePost() {
    if (!canEdit || !draft?.id) return
    try {
      setPosting(true)
      const result = await postJournalEntry(draft.id)
      setDraft({
        ...draft,
        status: result.draft.status,
        qbo_url: result.draft.qbo_url,
        qbo_entity_id: result.draft.qbo_entity_id,
      })
      await loadMonthsSummary()
      toast.success(`Posted ${result.draft.doc_number} to QuickBooks`)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to post journal entry'))
    } finally {
      setPosting(false)
    }
  }

  async function handleDeleteQbo() {
    if (!canEdit || !draft?.id) return
    const baseConfirm = draft.doc_number
      ? `Delete ${draft.doc_number} from QuickBooks?`
      : 'Delete this journal entry from QuickBooks?'
    const confirmed = window.confirm(
      `${baseConfirm} This will also uncheck the Allocated benefits income allocation readiness item.`,
    )
    if (!confirmed) return

    setDeleting(true)
    setPostedMenuOpen(false)
    try {
      const result = await deleteUhcJournalEntryFromQbo(draft.id)
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              ...result.draft,
              status: result.draft.status,
              qbo_url: result.draft.qbo_url ?? null,
              qbo_entity_id: result.draft.qbo_entity_id ?? null,
            }
          : result.draft,
      )
      if (selectedMonth) {
        await loadMonthDetail(selectedMonth)
      }
      await loadMonthsSummary()
      toast.success('Journal entry deleted from QuickBooks')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete journal entry from QuickBooks'))
    } finally {
      setDeleting(false)
    }
  }

  async function handleValidateBank() {
    if (!jeDate || !audit?.allocation_after_adjustments?.total) return
    try {
      setValidatingBank(true)
      setBankStatus(null)
      const result = await validateUhcBankTransaction({
        je_date: jeDate,
        amount_paid: audit.allocation_after_adjustments.total,
      })
      setBankStatus(result.matched ? `✓ ${result.message}` : `✗ ${result.message}`)
    } catch (err) {
      setBankStatus(formatApiError(err, 'Bank validation failed'))
    } finally {
      setValidatingBank(false)
    }
  }

  const combinedTotal = audit?.allocation_after_adjustments?.total
  const balanced =
    draft != null &&
    Math.abs(Number(draft.total_debits) - Number(draft.total_credits)) < 0.005
  const isPosted = draft?.status === 'posted'
  const draftTxnDateLabel = formatJePostedDate(draft?.txn_date ?? null)
  const files = monthDetail?.files ?? []
  const monthLabel = selectedMonth ? reportMonthLabel(selectedMonth) : ''

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <ReportMonthSelect
          id="uhc-allocate-month"
          options={monthSelectOptions}
          value={selectedMonth || null}
          onChange={(id) => id && setSelectedMonth(id)}
          disabled={!monthsSummaryReady || !monthSelectOptions.length}
          placeholder="Select month"
          className="w-[300px] max-w-full text-sm"
        />
        {monthLoading ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
            Loading {monthLabel}…
          </span>
        ) : null}
      </div>

      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="container-title">Invoice files</h2>
        <p className="text-xs text-gray-500">
          UHC eServices → Billing &amp; Payment → Invoices → Closed Invoice Listing — download Detail
          CSV for {monthLabel || 'the selected month'}.
        </p>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <label className="btn btn-secondary text-sm cursor-pointer">
              <CloudArrowUpIcon className="h-4 w-4" />
              {uploading ? 'Uploading…' : 'Upload invoice CSV'}
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                disabled={uploading || !selectedMonth}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (file) void handleUpload('invoice', file)
                }}
              />
            </label>
            <label className="btn btn-secondary text-sm cursor-pointer">
              <CloudArrowUpIcon className="h-4 w-4" />
              Add adjustment CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                disabled={uploading || !selectedMonth}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (file) void handleUpload('adjustment', file)
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary text-sm"
              onClick={() => void handleAudit()}
              disabled={auditing || !files.length}
            >
              <DocumentMagnifyingGlassIcon className="h-4 w-4" />
              {auditing ? 'Auditing…' : 'Audit invoice(s)'}
            </button>
          </div>
        ) : null}

        {files.length > 0 ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium text-gray-900">{file.filename}</span>
                  <span className="ml-2 text-xs text-gray-500 capitalize">({file.kind})</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="btn btn-secondary h-8 px-2 text-xs"
                    onClick={() => void handleDownload(file.id, file.filename)}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download
                  </button>
                  {canEdit ? (
                    <button
                      type="button"
                      className="btn btn-secondary h-8 px-2 text-xs"
                      onClick={() => void handleDeleteFile(file.id)}
                      aria-label={`Delete ${file.filename}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No files uploaded for this month yet.</p>
        )}
      </div>

      {audit ? (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="container-title">Audit</h2>
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                audit.audit_status === 'ready'
                  ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
                  : audit.audit_status === 'ready_with_notes'
                    ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-100'
                    : 'bg-red-50 text-red-800 ring-1 ring-red-100'
              }`}
            >
              {audit.audit_status === 'ready'
                ? 'Ready'
                : audit.audit_status === 'ready_with_notes'
                  ? 'Ready with notes'
                  : 'Not ready'}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
            {audit.invoice_number ? <span>Invoice {audit.invoice_number}</span> : null}
            {audit.invoice_date ? <span>Invoice date {audit.invoice_date}</span> : null}
            {combinedTotal != null ? <span>Combined total {formatUsd(combinedTotal)}</span> : null}
          </div>

          {!audit.employee_locations.is_valid ? (
            <UhcAuditEmployeeIssues
              validation={audit.employee_locations}
              onAddMissing={onAddMissing}
              addingMissing={addingMissing}
            />
          ) : (
            <AllocationPreviewCards
              before={audit.allocation_before_adjustments}
              after={audit.allocation_after_adjustments}
            />
          )}
          <UhcAuditNotesList notes={audit.notes} />

          <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:max-w-xs">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-600">
                Date payment posted
              </label>
              <DateInput
                value={jeDate}
                onChange={setJeDate}
                className="input text-sm"
                dateFormat="iso"
                disabled={!canEdit}
              />
            </div>
            {canEdit && combinedTotal != null ? (
              <button
                type="button"
                className="btn btn-secondary text-sm justify-self-start"
                onClick={() => void handleValidateBank()}
                disabled={validatingBank || !jeDate}
              >
                {validatingBank ? 'Checking…' : 'Validate bank transaction'}
              </button>
            ) : null}
            {bankStatus ? <p className="text-sm text-gray-700">{bankStatus}</p> : null}
          </div>
        </div>
      ) : null}

      <JournalEntryCard
        className="w-full"
        title="Benefits reallocation"
        posted={isPosted}
        identity={
          <>
            {draft?.doc_number ? (
              <JournalEntryDocChip>{draft.doc_number}</JournalEntryDocChip>
            ) : null}
            {draftTxnDateLabel ? <span>{draftTxnDateLabel}</span> : null}
            {isPosted && draft?.qbo_url ? (
              <JournalEntryExternalLink href={draft.qbo_url}>
                View in QuickBooks
              </JournalEntryExternalLink>
            ) : null}
          </>
        }
        actions={
          <>
            {canEdit && audit && !audit.has_blockers ? (
              <button
                type="button"
                className="btn btn-primary text-xs"
                onClick={() => void handleCreateDraft()}
                disabled={creatingDraft || !jeDate}
              >
                {creatingDraft
                  ? 'Saving…'
                  : draft
                    ? isPosted
                      ? 'Regenerate'
                      : 'Regenerate'
                    : 'Generate'}
              </button>
            ) : null}
            {draft && !isPosted && canEdit ? (
              <button
                type="button"
                className="btn btn-primary text-xs"
                onClick={() => void handlePost()}
                disabled={posting || !balanced}
              >
                {posting ? 'Posting…' : 'Post to QuickBooks'}
              </button>
            ) : null}
            {isPosted ? (
              <JournalEntryPostedBadge
                loading={posting || deleting}
                menuOpen={postedMenuOpen}
                onMenuOpenChange={setPostedMenuOpen}
                menu={
                  canEdit ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                      disabled={deleting || creatingDraft}
                      onClick={() => void handleDeleteQbo()}
                    >
                      Delete Existing JE
                    </button>
                  ) : undefined
                }
              />
            ) : null}
          </>
        }
        alerts={
          draft?.warnings.length
            ? draft.warnings.map((warning, index) => (
                <WarningBanner key={index} message={warning} />
              ))
            : undefined
        }
      >
        {draft ? (
          <JournalEntryPreview
            lines={draft.lines}
            variant="split"
            hideHeader
            totalDebits={draft.total_debits}
            totalCredits={draft.total_credits}
          />
        ) : (
          <p className="text-sm text-gray-500">
            Upload and audit invoice CSVs, then generate the journal entry.
          </p>
        )}
      </JournalEntryCard>
    </div>
  )
}
